import React, { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { StyledTable, TitleBar, SnackDisplay, LoadingBackDrop } from "@lp/lp-ui";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useCache from "../../app/useCache";
import { CachedEntity, CacheLookup, fetchRoles } from "../../app/cacheSlice";
import { AuditLog, AuditLogDetails, PaginationResponse, RoleModel } from "../../typings";
import { isSystemRole } from "../../constants";
import identityService from "../../services/IdentityService";
import { AuditLogDialog } from "../common/AuditLogDialog";
import RoleDialog from "./RoleDialog";
import RolesDocDialog from "./RolesDocDialog";

const Roles = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((store) => store.currentUser);
  const roles = useCache(CachedEntity.Role);
  const [filterString, setFilterString] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleModel | null>(null);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState<"success" | "error">("success");
  const [auditRole, setAuditRole] = useState<{ id: number; name: string } | null>(null);
  const [docOpen, setDocOpen] = useState(false);

  const auditRoleId = auditRole?.id;

  const showAuditError = (message: string) => {
    setSnackColor("error");
    setSnackMessage(message);
  };

  const loadAuditLogs = useCallback(async (): Promise<PaginationResponse<AuditLog> | null> => {
    if (!auditRoleId) return null;
    try {
      const response = await identityService.getRoleAuditLogs(auditRoleId);
      return response.data;
    } catch {
      showAuditError("Failed to load audit logs.");
      return null;
    }
  }, [auditRoleId]);

  const loadAuditLogDetails = useCallback(
    async (auditLogId: number): Promise<AuditLogDetails | null> => {
      if (!auditRoleId) return null;
      try {
        const response = await identityService.getRoleAuditLogDetails(auditRoleId, auditLogId);
        return response.data;
      } catch {
        showAuditError("Failed to load audit log details.");
        return null;
      }
    },
    [auditRoleId]
  );

  const isLoadingCurrentUser = currentUser.data === undefined;
  const isTenantAdmin = currentUser.data?.isTenantAdmin === true;

  // Wait for the current user to load before deciding access — otherwise the
  // first render (data not yet populated) would bounce a legitimate TenantAdmin.
  if (isLoadingCurrentUser) {
    return <LoadingBackDrop open />;
  }

  // Defense in depth: the nav link is already hidden for non-tenant-admins.
  if (!isTenantAdmin) {
    return <Navigate to="/" replace />;
  }

  const openCreate = () => {
    setSelectedRole(null);
    setDialogOpen(true);
  };

  const openEdit = (role: CacheLookup) => {
    setSelectedRole({ id: role.id, name: role.key, description: role.description });
    setDialogOpen(true);
  };

  const handleDialogClose = (savedMessage?: string) => {
    setDialogOpen(false);
    setSelectedRole(null);
    if (savedMessage) {
      dispatch(fetchRoles());
      setSnackColor("success");
      setSnackMessage(savedMessage);
    }
  };

  const filtered = roles
    .filter(
      (r) =>
        r.label.toLowerCase().includes(filterString.toLowerCase()) ||
        (r.description ?? "").toLowerCase().includes(filterString.toLowerCase())
    )
    // System roles first, then custom roles; alphabetical within each group.
    .sort((a, b) => {
      const aSystem = isSystemRole(a.key) ? 0 : 1;
      const bSystem = isSystemRole(b.key) ? 0 : 1;
      return aSystem - bSystem || a.label.localeCompare(b.label);
    });

  return (
    <React.Fragment>
      <TitleBar
        title="Roles"
        subtitle={`${roles.length} records found`}
        actionComponents={[
          // TitleBar drops actionComponents into a plain Box, so the flex wrapper here
          // is what keeps the button and the info icon from sitting flush together.
          <Box key="roles-actions" display="flex" gap={1} alignItems="center">
            <Button variant="outlined" onClick={openCreate}>
              New Role
            </Button>
            <Tooltip title="Roles documentation">
              <IconButton
                size="small"
                aria-label="Roles documentation"
                onClick={() => setDocOpen(true)}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>,
        ]}
      />
      <Paper>
        <Grid container spacing={1} m={1}>
          <Grid item xs={4}>
            <TextField
              placeholder="Quick Search"
              variant="outlined"
              size="small"
              value={filterString}
              fullWidth
              autoComplete="off"
              onChange={(e) => setFilterString(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <StyledTable stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Audit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((role) => (
              <TableRow
                hover
                key={role.id}
                sx={{ cursor: "pointer" }}
                onClick={() => openEdit(role)}
              >
                <TableCell>{role.label}</TableCell>
                <TableCell>{role.description}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View audit logs">
                    <IconButton
                      size="small"
                      aria-label={`View audit logs for ${role.label}`}
                      // The row opens the edit dialog, so keep the click from reaching it.
                      onClick={(e) => {
                        e.stopPropagation();
                        setAuditRole({ id: role.id, name: role.label });
                      }}
                    >
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </Paper>

      <RoleDialog open={dialogOpen} role={selectedRole} handleClose={handleDialogClose} />

      <RolesDocDialog open={docOpen} handleClose={() => setDocOpen(false)} />

      <AuditLogDialog
        open={auditRole !== null}
        subject={auditRole?.name}
        handleClose={() => setAuditRole(null)}
        loadAll={loadAuditLogs}
        loadDetails={loadAuditLogDetails}
      />

      {snackMessage && (
        <SnackDisplay snack={snackMessage} color={snackColor} handleClose={() => setSnackMessage("")} />
      )}
    </React.Fragment>
  );
};

export default Roles;
